'use client';

import { useEffect, useState } from 'react';

import { useClerk } from '@clerk/nextjs';

import { useClerkKillSwitch } from './use-clerk-kill-switch';

/** How long to wait for Clerk JS to load before treating it as degraded. */
const CLERK_LOAD_TIMEOUT_MS = 8_000;

/**
 * Returns Clerk availability status: whether it's still loading,
 * and whether it ended up in a degraded/error state.
 *
 * Detects two failure modes:
 * 1. Clerk's JS bundle fails to load → status stays 'loading', timeout fires
 * 2. Clerk SDK reports 'degraded' or 'error' status
 */
export function useClerkStatus(): {
  isDegraded: boolean;
  isClerkLoading: boolean;
} {
  const { status } = useClerk();
  const [timedOut, setTimedOut] = useState(false);

  // Timeout fallback: Clerk's status can stay 'loading' forever when
  // its JS bundle fails to load (the ClerkRuntimeError fires but the
  // React status is never updated to 'error').
  useEffect(() => {
    if (status !== 'loading') return;
    const timer = setTimeout(() => setTimedOut(true), CLERK_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [status]);

  const isDegraded = status === 'degraded' || status === 'error' || timedOut;
  const isClerkLoading = status === 'loading' && !timedOut;

  return { isDegraded, isClerkLoading };
}

/**
 * Convenience wrapper — returns true when Clerk is unavailable from
 * the user's perspective. Three triggers:
 *
 * 1. Clerk SDK reports 'degraded' or 'error'.
 * 2. Clerk JS bundle fails to load within the timeout.
 * 3. The PostHog `enable-clerk-kill-switch` flag is on (operator-
 *    initiated drain). Including the flag here lets the health
 *    status banner light up for logged-in users alongside the
 *    maintenance card on anonymous surfaces.
 */
export function useClerkDegraded(): boolean {
  const { isDegraded } = useClerkStatus();
  const killSwitchActive = useClerkKillSwitch();
  return isDegraded || killSwitchActive;
}
